import cn from 'classnames';
import Button from '@/components/ui/button';
import NotFound from '@/components/ui/not-found';
import rangeMap from '@/lib/range-map';
import ProductCard from '@/components/products/cards/card';
import ErrorMessage from '@/components/ui/error-message';
import { useProducts } from '@/framework/product';
import { PRODUCTS_PER_PAGE } from '@/framework/client/variables';
import type { Product } from '@/types';
import type { ReactNode } from 'react';
import type { HomeListContext } from '@/lib/gtm';
import ProductCardLoader from '../ui/loaders/product-card-loader';

interface Props {
  limit?: number;
  sortedBy?: string;
  orderBy?: string;
  column?: 'five' | 'six' | 'auto' | 'four';
  shopId?: string;
  gridClassName?: string;
  products: Product[] | undefined;
  isLoading?: boolean;
  error?: any;
  loadMore?: any;
  isLoadingMore?: boolean;
  hasMore?: boolean;
  className?: string;
  categoryName?: string;
  /** Rendered above the empty-state illustration when there are no products (e.g. did-you-mean). */
  emptyAccessory?: ReactNode;
  listContext?: HomeListContext;
  /** Number of leading cards whose image loads eagerly with high priority (above-the-fold row). */
  priorityCount?: number;
}

export function Grid({
  className,
  gridClassName,
  products,
  isLoading,
  error,
  loadMore,
  isLoadingMore,
  hasMore,
  limit = PRODUCTS_PER_PAGE,
  column = 'auto',
  categoryName,
  emptyAccessory,
  listContext,
  priorityCount = 0,
}: Readonly<Props>) {
  if (error) return <ErrorMessage message={error.message} />;

  if (!isLoading && !products?.length) {
    return (
      <div className="md:w-full md:min-h-full px-4 pt-2 pb-8 lg:p-8">
        {emptyAccessory}
        <NotFound text="text-not-found" className="w-auto md:w-7/12 mx-auto" />
      </div>
    );
  }

  return (
    <div className={cn('w-full', className)}>
      {categoryName && (
        <div className="w-full flex justify-end mb-4">
          <p className="text-lg text-body">
            Você está vendo: <span className="font-semibold">{categoryName}</span>
          </p>
        </div>
      )}
      <div
        className={cn(
          {
            'grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] gap-3':
              column === 'auto',
            'grid grid-cols-4 gap-3': column === 'four',
            'grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-6 gap-y-10 lg:grid-cols-[repeat(auto-fill,minmax(200px,1fr))] xl:grid-cols-[repeat(auto-fill,minmax(220px,1fr))] xl:gap-8 xl:gap-y-11 2xl:grid-cols-5 3xl:grid-cols-[repeat(auto-fill,minmax(360px,1fr))]':
              column === 'five',
            'grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-4 md:gap-6 lg:grid-cols-[repeat(auto-fill,minmax(200px,1fr))] xl:grid-cols-[repeat(auto-fill,minmax(220px,1fr))] 2xl:grid-cols-5 3xl:grid-cols-[repeat(auto-fill,minmax(360px,1fr))]':
              column === 'six',
          },
          gridClassName,
        )}
      >
        {isLoading && !products?.length
          ? rangeMap(limit, (i) => (
            <ProductCardLoader key={i} uniqueKey={`product-${i}`} />
          ))
          : products?.map((product, index) => (
            <ProductCard
              product={product}
              key={product?.sku}
              listContext={listContext}
              imagePriority={index < priorityCount}
            />
          ))}
      </div>
      {hasMore && (
        <div className="flex justify-center mt-8 mb-4 sm:mb-6 lg:mb-2 lg:mt-12">
          <Button
            loading={isLoadingMore}
            onClick={loadMore}
            className="text-sm font-semibold h-11 md:text-base"
          >
            {"Carregar Mais"}
          </Button>
        </div>
      )}
    </div>
  );
}
interface ProductsGridProps {
  className?: string;
  gridClassName?: string;
  variables?: any;
  column?: 'five' | 'auto';
}
export default function ProductsGrid({
  className,
  gridClassName,
  variables,
  column = 'auto',
}: Readonly<ProductsGridProps>) {
  const { products, loadMore, isLoadingMore, isLoading, hasMore, error } =
    useProducts(variables);

  const productsItem: any = products;
  return (
    <Grid
      products={productsItem}
      loadMore={loadMore}
      isLoading={isLoading}
      isLoadingMore={isLoadingMore}
      hasMore={hasMore}
      error={error}
      className={className}
      gridClassName={gridClassName}
      column={column}
    />
  );
}
